/**
 * Schedule Collaboration KPI section for the Dashboard DefaultTab.
 *
 * Gated by the `schedule_collaboration` feature flag (Dennis Eagle and any
 * other supplier that opts in). Renders a 3-card KPI row summarising
 * Needs Action / On Hold / Arrears schedule counts plus a quick link into
 * the schedules tab on the Purchasing page.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { AlertCircle, PauseCircle, Clock, ArrowRight } from 'lucide-react';
import { purchasingAPI } from '../../lib/api';

interface ScheduleKPIs {
  needsAction: number;
  onHold: number;
  arrears: number;
}

/**
 * Returns today as YYYY-MM-DD for use in schedule_date__lt filters.
 */
function todayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Schedule Collaboration KPI section. Only renders when the parent has
 * already verified the `schedule_collaboration` feature flag.
 */
export function ScheduleCollaborationSection() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState<ScheduleKPIs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchKPIs = async () => {
      try {
        setLoading(true);
        setError(null);
        const today = todayISO();
        const [needsActionRes, onHoldRes, arrearsRes] = await Promise.all([
          purchasingAPI.getSchedules({ page_size: 1, needs_action: true }),
          purchasingAPI.getSchedules({ page_size: 1, on_hold: true }),
          purchasingAPI.getSchedules({ page_size: 1, schedule_date__lt: today }),
        ]);
        if (cancelled) return;
        setKpis({
          needsAction: needsActionRes.data.count || 0,
          onHold: onHoldRes.data.count || 0,
          arrears: arrearsRes.data.count || 0,
        });
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load schedule KPIs';
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchKPIs();
    return () => {
      cancelled = true;
    };
  }, []);

  const cardClass = 'shadow-sm';
  const valueClass = 'text-2xl font-bold';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Schedule Collaboration</h3>
        <Button
          variant="ghost"
          size="sm"
          className="text-sm"
          onClick={() => navigate('/purchasing?tab=schedules')}
        >
          View all schedules <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className={`${cardClass} border-amber-200`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              Needs Action
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : error ? (
              <p className="text-sm text-red-600">{error}</p>
            ) : kpis && kpis.needsAction > 0 ? (
              <p className={`${valueClass} text-amber-600`}>{kpis.needsAction.toLocaleString()}</p>
            ) : (
              <p className="text-sm text-muted-foreground">No schedules needing action</p>
            )}
          </CardContent>
        </Card>

        <Card className={`${cardClass} border-blue-200`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <PauseCircle className="h-4 w-4 text-blue-500" />
              On Hold
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : kpis && kpis.onHold > 0 ? (
              <p className={`${valueClass} text-blue-600`}>{kpis.onHold.toLocaleString()}</p>
            ) : (
              <p className="text-sm text-muted-foreground">No schedules on hold</p>
            )}
          </CardContent>
        </Card>

        <Card className={`${cardClass} border-red-200`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-red-500" />
              Arrears
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : kpis && kpis.arrears > 0 ? (
              <p className={`${valueClass} text-red-600`}>{kpis.arrears.toLocaleString()}</p>
            ) : (
              <p className="text-sm text-muted-foreground">No past-due schedules</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
